'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
  { label: 'Home', href: '/homepage' },
  { label: 'Apartments', href: '/apartment-categories' },
  { label: 'Services', href: '/amenities-and-services' },
  { label: 'Book Now', href: 'https://bookingengine.stayflexi.com/?hotel_id=33526' },
  { label: 'Corporate Solutions', href: '/corporate-solutions' },
  { label: 'Gallery', href: '/gallery' }];


  const moreItems = [
  { label: 'About Us', href: '/about-us' },
  { label: 'Location Guide', href: '/location-guide' },
  { label: 'Booking Policy', href: '/booking-policy' },
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact Us', href: '/contact-us' }];


  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-border shadow-subtle ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          <Link href="/homepage" className="flex items-center space-x-3 flex-shrink-0">
            <div className="flex flex-col items-start">
              <div className="relative w-[200px] h-12 flex-shrink-0">
                <AppImage
                  src="/assets/images/002-1767601217860.png"
                  alt="Proximus Logo"
                  width={200}
                  height={48}
                  className="object-contain w-full h-full" />

              </div>
              <span className="text-xs font-medium text-text-secondary -mt-2">
                Boutique Hotel Apartments
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            {navigationItems.map((item) => {
              const isExternal = item.href.startsWith('http');
              return isExternal ? (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-md transition-all duration-300 text-left ml-0.5">
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-md transition-all duration-300 text-left ml-0.5">
                  {item.label}
                </Link>
              );
            })}
            
            <div className="relative">
              <button
                onClick={toggleMoreMenu}
                className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-md transition-all duration-300 flex items-center space-x-1">

                <span>More</span>
                <Icon
                  name={isMoreMenuOpen ? "ChevronUpIcon" : "ChevronDownIcon"}
                  size={16}
                  className="text-current" />

              </button>
              
              {isMoreMenuOpen &&
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-premium border border-border py-2 animate-scale-in">
                  {moreItems.map((item) =>
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMoreMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-300">

                      {item.label}
                    </Link>
                )}
                </div>
              }
            </div>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <a href="https://bookingengine.stayflexi.com/?hotel_id=33526" target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 text-sm font-semibold text-white hover:bg-accent/90 rounded-lg shadow-subtle hover:shadow-elevated transition-all duration-300 hover:scale-105 bg-[rgba(207,164,11,1)]">
              Check Availability
            </a>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-text-secondary hover:text-primary hover:bg-muted rounded-md transition-colors duration-300"
            aria-label="Toggle mobile menu">

            <Icon name={isMobileMenuOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
          </button>
        </div>

        {isMobileMenuOpen &&
        <div className="lg:hidden bg-white border-t border-border animate-slide-in-left">
            <nav className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => {
                const isExternal = item.href.startsWith('http');
                return isExternal ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-lg transition-colors duration-300">
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-lg transition-colors duration-300">
                    {item.label}
                  </Link>
                );
              })}
              
              {moreItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted rounded-lg transition-colors duration-300">

                  {item.label}
                </Link>
            )}
              
              <div className="pt-4 border-t border-border">
                <a href="https://bookingengine.stayflexi.com/?hotel_id=33526" target="_blank" rel="noopener noreferrer" className="block w-full px-6 py-3 text-sm font-semibold text-white bg-accent hover:bg-accent/90 rounded-lg shadow-subtle transition-all duration-300 text-center">
                  Check Availability
                </a>
              </div>
            </nav>
          </div>
        }
      </div>
    </header>);

};

export default Header;